import './balancer-transactions.scss';
import { Enum, PaginationList } from '../../../../core/domain';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { getIconPath } from '../../../../core/utils/getIconPath';
import { Divider, Dropdown, Menu, message, Tag } from 'antd';
import { digitSperator, faNumToEnNum } from '../../../../core/utils/decimalExtentions';
import moment from 'jalali-moment';
import { Badge } from '../../../../components';
import { DeleteOutlined, EditOutlined, InfoCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { ReactComponent as CalendarIcon } from 'assets/icons/dash-line-icons/calendar.svg';
import { getOrderSides } from '../../../../core/repositories/orders';
import { getPageNum } from '../../../../core/utils/paggingHelper';
import { Card } from '../../../../components/card/card';
import { Input, inputType } from '../../../../components/input/input';
import { Button, buttonType } from '../../../../components/button/button';
import { Table } from '../../../../components/table/table';
import { Dropdown as CustomDropdown } from 'components/dropdown/dropdown';
import DatePicker from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import * as React from 'react';
import { Page } from 'components/page/page';
import { BadgeType } from 'components/badge/badge';
import { BalancerTransaction } from 'core/domain/balancer-transaction/balancer-transaction';
import { useNavigate } from 'react-router-dom';
import { getAllBalancerTransactions, removeBalancerTransaction } from 'core/repositories/balancer-transaction';
import { jFormat } from 'core/utils/jFormat';

const initialFilters = {
    marketType: {
        id: -1,
        title: 'نوع ارز',
    },
    state: {
        id: -1,
        title: 'همه وضعیت ها',
    },
    type: {
        id: -1,
        title: 'همه سفارش ها',
    },
    source: {
        id: -1,
        title: 'همه مبدا ها',
    },
    side: {
        id: -1,
        title: 'سمت معامله',
    },
};

export const BalancerTransactions = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [transactions, setTransactions] = useState<PaginationList<BalancerTransaction>>();
    const [selectedTransaction, setSelectedTransaction] = useState<BalancerTransaction>();

    const [deleteModalIsOpen, setDeleteModalIsOpen] = useState<boolean>(false);
    const [deleteModalIsLoading, setDeleteModalIsLoading] = useState<boolean>(false);

    //Enums
    const [orderSides, setOrderSides] = useState<Enum[]>([initialFilters.side]);

    const [pageSize, setPageSize] = useState<number>(10);

    const [query, setQuery] = useState<string>('');

    const [filterValues, setFilterValues] = useState<{
        startDate?: string;
        endDate?: string;
        side: Enum;
    }>({
        side: initialFilters.side,
    });

    const navigate = useNavigate();

    const startDateRef = useRef();
    const endDateRef = useRef();

    const columns: any[] = [
        {
            name: 'نماد',
            id: 'symbol',
            selector: (row: BalancerTransaction) => row.symbol.toUpperCase(),
            width: '15%',
        },
        {
            name: 'قیمت خرید/فروش',
            id: 'onPrice',
            selector: (row: BalancerTransaction) => row.onPrice,
            cell: (row: BalancerTransaction) => digitSperator(row.onPrice),
            width: '15%',
        },
        {
            name: 'مقدار',
            id: 'amount',
            selector: (row: BalancerTransaction) => row.amount,
            cell: (row: BalancerTransaction) => digitSperator(row.amount),
            width: '15%',
        },
        {
            name: 'نوع معامله',
            id: 'side',
            selector: (row: BalancerTransaction) => row.side,
            cell: (row: BalancerTransaction) => (
                <Tag color={`${row.side.id === 1 ? 'green' : 'red'}`}>{row.side.id === 1 ? 'خرید' : 'فروش'}</Tag>
            ),
            width: '15%',
        },
        {
            name: 'تاریخ ایجاد',
            id: 'createdOn',
            selector: (row: BalancerTransaction) => row.createdOn,
            cell: (row: BalancerTransaction) => (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span className="day">{moment(row.createdOn).locale('fa').local().format('jYYYY/MM/DD')}</span>
                    <span className="time">{moment(row.createdOn).local().format('HH:mm')}</span>
                </div>
            ),
            width: '20%',
        },
        {
            name: 'عملیات',
            id: 'actions',
            cell: (row: BalancerTransaction) => (
                <div className="actions">
                    <EditOutlined onClick={() => navigate(`${row.id}`)} />

                    <DeleteOutlined
                        onClick={() => {
                            setSelectedTransaction(row);
                            setDeleteModalIsOpen(true);
                        }}
                    />
                </div>
            ),
        },
    ];

    const handleDelete = async () => {
        if (!selectedTransaction) return;
        try {
            removeBalancerTransaction(setDeleteModalIsLoading, selectedTransaction.id, () => {
                setDeleteModalIsOpen(false);
                fetchData();
            });
        } catch {
            message.error('خطا در حذف تراکنش');
        }
    };

    const handleEnums = async () => {
        await getOrderSides(setLoading, setOrderSides);
    };

    const fetchData = async () => {
        try {
            getAllBalancerTransactions(setLoading, setTransactions, 1, pageSize, query);
        } catch (e) {
            message.error('خطا در بارگیری اطلاعات');
        } finally {
            setLoading(false);
        }
    };

    const filterHandle = () => {
        var q = '';

        if (filterValues.side.id !== -1) q += `${q.includes('?') ? '&' : '?'}side=${filterValues.side.id}`;
        if (filterValues.startDate !== '')
            q += `${q.includes('?') ? '&' : '?'}startDate=${jFormat(filterValues.startDate)}`;
        if (filterValues.endDate !== '')
            q += `${q.includes('?') ? '&' : '?'}endDate=${jFormat(filterValues.endDate)}`;
        setQuery(q);
        getAllBalancerTransactions(setLoading, setTransactions, 1, pageSize, q);
    };

    useEffect(() => {
        handleEnums();
        fetchData();
    }, []);

    return (
        <Page className="pmx-balancer-transactions" title="مدیریت تامین موجودی">
            <Card title="مدیریت تامین موجودی" blur={loading}>
                <div className="filter-row">
                    <div className="fields">
                        <CustomDropdown
                            items={orderSides}
                            onChangeHandle={(value) =>
                                setFilterValues({
                                    ...filterValues,
                                    side: orderSides?.find((l) => l.id === value),
                                })
                            }
                            selectedOption={filterValues.side}
                            defaultSelected={filterValues.side}
                        />

                        <DatePicker
                            className="date-picker"
                            value={filterValues.startDate}
                            calendar={persian}
                            ref={startDateRef}
                            locale={persian_fa}
                            render={
                                <div>
                                    <Input
                                        value={filterValues.startDate}
                                        icon={<CalendarIcon />}
                                        placeholder="از تاریخ"
                                    />
                                </div>
                            }
                            onChange={(e) => {
                                setFilterValues({
                                    ...filterValues,
                                    startDate: e.format(),
                                });
                            }}
                        />
                        <DatePicker
                            className="date-picker"
                            value={filterValues.endDate}
                            calendar={persian}
                            ref={endDateRef}
                            locale={persian_fa}
                            render={
                                <div>
                                    <Input
                                        value={filterValues.endDate}
                                        icon={<CalendarIcon />}
                                        placeholder="تا تاریخ"
                                    />
                                </div>
                            }
                            onChange={(e) => {
                                setFilterValues({
                                    ...filterValues,
                                    endDate: e.format(),
                                });
                            }}
                        />
                    </div>

                    <Divider type="vertical" />
                    <div className="buttons">
                        <Button
                            type={buttonType.info}
                            icon={<PlusCircleOutlined />}
                            text="افزودن"
                            onClick={() => navigate('add')}
                        />
                        <Button
                            loading={loading}
                            disabled={loading}
                            onClick={() => filterHandle()}
                            text="اعمال فیلتر"
                            type={buttonType.success}
                        />
                        <Button
                            type={buttonType.error}
                            text={'حذف فیلتر'}
                            onClick={() => {
                                setFilterValues({
                                    startDate: '',
                                    endDate: '',
                                    side: initialFilters.side,
                                });
                                getAllBalancerTransactions(setLoading, setTransactions, getPageNum(), pageSize, '');
                            }}
                        />
                    </div>
                </div>
                {transactions && (
                    <Table
                        filters={query}
                        setloader={setLoading}
                        setdata={setTransactions}
                        getdata={getAllBalancerTransactions}
                        pagination={transactions.pagination}
                        data={transactions.items}
                        column={columns}
                        setPageSize={setPageSize}
                        pageSize={transactions?.pagination?.pageSize}
                        className={'balancer-transactions-table'}
                    />
                )}
            </Card>
        </Page>
    );
};
